import CRMTabs from '@components/Common/CRMTabs';
import { chartColor } from '@components/Common/Charts/chartPalette';
import EmptyState from '@components/Common/EmptyState';
import MetricTable from '@components/Common/MetricTable';
import Panel from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { getSignupStatsQuery } from '@constants/graphQLQueries';
import dayjs from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import React, { useEffect, useState } from 'react';
import './index.scss';
import { COUNT_Y_AXIS } from '@components/Common/Charts/chartAxes';

interface SignupDay { day: string; jobSeekers: number; employers: number }
interface Breakdown { key: string; jobSeekers: number; employers: number }
interface SignupStats {
  jobSeekers: number;
  employers: number;
  days: SignupDay[];
  channels: Breakdown[];
  methods: Breakdown[];
}

const RANGES = [7, 30, 90];
const num = (n: number) => n.toLocaleString('en-IN');

const breakdownColumns = (first: string) => [
  { key: 'key', header: first, variant: 'name' as const, render: (r: Breakdown) => r.key, sortValue: (r: Breakdown) => r.key },
  { key: 'jobSeekers', header: 'Job seekers', render: (r: Breakdown) => num(r.jobSeekers), sortValue: (r: Breakdown) => r.jobSeekers },
  { key: 'employers', header: 'Employers', render: (r: Breakdown) => num(r.employers), sortValue: (r: Breakdown) => r.employers },
];

/**
 * Sign-ups: people creating their own accounts, per day, and where they came from.
 *
 * The Platform Health tiles count every record added, CV imports included, so a batch import
 * reads as a surge of sign-ups there. This counts only self sign-ups. Channel and method come
 * from what the site records at sign-up; accounts made before it did read "Not tracked".
 */
const SignupsSection: React.FC = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<SignupStats | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setError(false);
    fetchGraphQLApiData(getSignupStatsQuery(days))
      .then((r) => { if (live) setStats(r?.data?.getSignupStats ?? null); })
      .catch(() => { if (live) { setStats(null); setError(true); } });
    return () => { live = false; };
  }, [days]);

  return (
    <Panel className="db-section">
      <Box className="db-section-head">
        <Box className="db-section-header">
          <Typography className="db-section-title" variant="subtitle1">Sign-ups</Typography>
          <Typography className="db-section-sub" variant="body2">
            {stats
              ? `${num(stats.jobSeekers)} job seekers and ${num(stats.employers)} employers signed up in the last ${days} days. Imports are not counted.`
              : 'People who created their own account. Imports are not counted.'}
          </Typography>
        </Box>
        {/* Inline: a selector inside a card must never be sticky (see CRMTabs `variant`). */}
        <CRMTabs
          variant="inline"
          tabs={RANGES.map((d) => ({ label: `${d} days` }))}
          value={RANGES.indexOf(days)}
          onChange={(i) => setDays(RANGES[i])}
        />
      </Box>

      {error ? (
        <EmptyState compact title="Sign-ups could not be loaded" body="Try again in a moment." />
      ) : !stats ? (
        <SkeletonLoader />
      ) : (
        <>
          {stats.jobSeekers + stats.employers > 0 ? (
            <BarChart
              xAxis={[{ data: stats.days.map((d) => dayjs(d.day).format('D MMM')), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
              series={[
                { data: stats.days.map((d) => d.jobSeekers), label: 'Job seekers', stack: 'signups', color: chartColor('--acc') },
                { data: stats.days.map((d) => d.employers), label: 'Employers', stack: 'signups', color: chartColor('--teal') },
              ]}
              yAxis={COUNT_Y_AXIS}
              height={260}
              margin={{ top: 20, right: 16, bottom: 48, left: 44 }}
            />
          ) : (
            <EmptyState compact title={`No sign-ups in the last ${days} days`} />
          )}
          <Box className="db-breakdown-grid">
            <MetricTable<Breakdown>
              rows={stats.channels}
              getRowKey={(r) => r.key}
              columns={breakdownColumns('Where they came from')}
              defaultSort={{ key: 'jobSeekers', dir: 'desc' }}
              emptyLabel="No sign-ups in this period."
            />
            <MetricTable<Breakdown>
              rows={stats.methods}
              getRowKey={(r) => r.key}
              columns={breakdownColumns('How they signed up')}
              defaultSort={{ key: 'jobSeekers', dir: 'desc' }}
              emptyLabel="No sign-ups in this period."
            />
          </Box>
        </>
      )}
    </Panel>
  );
};

export default SignupsSection;
